import type { FC } from "react";
import { memo } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { DateDropdown } from "@ui/components/DateDropdown/DateDropdown";
import { InputLabel } from "@ui/components/Inputs/components/InputWrapper/InputLabel";
import { TextInput } from "@ui/components/Inputs/TextInput";
import { useTranslations } from "@ui/providers/useTranslations";

import { SupportingText } from "@jti/core-components";
import type { AgeVerificationMethodType } from "@jti/fe-common";

import styles from "./FieldsArray.module.scss";

type FieldsArrayProps = {
  fieldName?(name: string): string;
} & AgeVerificationMethodType;

export const FieldsArray: FC<FieldsArrayProps> = memo(({ fieldName = (name: string) => name, ...method }) => {
  const { __ } = useTranslations();
  const { control } = useFormContext();
  const AVP_PREFIX = "avp_";

  return (
    <>
      {method.fields.map((item) => {
        const ageVerificationLabel = __(`age_verification.form.${item.code}.label`, [], item.label);
        const avpFieldId = `${AVP_PREFIX}${item.code}`;
        const avpValuesFieldName = `avpValues.${item.code}`;

        if (item.type === "date") {
          return (
            <div key={avpFieldId} className={styles.datesInputWrapper}>
              <InputLabel htmlFor={avpFieldId} id={avpFieldId}>{`${ageVerificationLabel} ${
                item.is_required ? "*" : ""
              }`}</InputLabel>
              <DateDropdown
                fieldName={avpValuesFieldName}
                dropdownFieldPrefix={avpFieldId}
                required={item.is_required}
              />
              {method.isOptional && (
                <SupportingText data-testid="textInputInfoIcon">
                  {__("auth.registration.age_verification.optional_message")}
                </SupportingText>
              )}
            </div>
          );
        }

        return (
          <Controller
            key={item.code}
            control={control}
            name={fieldName(item.code)}
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                isError={!!fieldState.error}
                errorText={fieldState.error?.message}
                helperText={__("auth.registration.age_verification.optional_message")}
                label={ageVerificationLabel}
                data-testid={item.code}
                placeholder={__(`age_verification.form.${item.code}.hint`, [], item.hint)}
                required={item.is_required}
              />
            )}
            rules={{
              required: {
                value: item.is_required && !method.isOptional,
                message: __(
                  `auth.age_verification.method_fields.${item.code}.validation.field_required`,
                  undefined,
                  __("validation.field_required")
                ),
              },
              ...(item.validation && {
                pattern: {
                  value: new RegExp(item.validation),
                  message: __("ageconfirmation.pattern.error"),
                },
              }),
            }}
          />
        );
      })}
    </>
  );
});
